import { Injectable } from '@angular/core';
import { ApiService, Pagination, PER_PAGE } from './api.service';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable, shareReplay, Subject, switchMap, take } from 'rxjs';
import { Paginate } from '@shared/entities/paginate';
import { Learning } from '@shared/entities/learning';
import { LanguageService } from '@shared/services';

export interface LearningFilterURL {
  keyword?: string;
  id?: string;
  title?: string;
  content?: string;
  complexity?: string;
  machineType?: string;
  tag?: string;
  active?: boolean;
  isFavorite?: boolean;
  previewId?: string;
  orderBy?: string;
  direction?: 'ASC' | 'DESC';
  locationId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LearningService extends ApiService<Learning> {
  private _refresh$: Subject<Pagination>;
  allLearning$: Observable<Paginate<Learning>>;
  private _currentPaginator: Pagination;
  private _currentFilters: LearningFilterURL = {};

  set filters(value: LearningFilterURL) {
    this._currentFilters = value;
    this.refresh(1, PER_PAGE);
  }

  constructor(
    private _activatedRoute: ActivatedRoute,
    private languageService: LanguageService
  ) {
    const masterUrl = '/learning';
    const detailUrl = '/learning';

    super(Learning, masterUrl, detailUrl);

    this._currentPaginator = { page: 1, perPage: PER_PAGE };

    this._refresh$ = new BehaviorSubject<Pagination>(this._currentPaginator);
    this.allLearning$ = this._refresh$.pipe(
      switchMap((pagination) => {
        this.removeUrlParameters();

        const currentLanguage = this.languageService.getCurrent().toUpperCase();
        this.setUrlParameter('language', currentLanguage);

        const { locationId } = this._activatedRoute.snapshot.queryParams;
        if (locationId) {
          this.setUrlParameter('locationId', locationId);
        }
        if (this._currentFilters.keyword !== undefined) {
          this.setUrlParameter('keyword', this._currentFilters.keyword);
        }
        if (this._currentFilters.id !== undefined) {
          this.setUrlParameter('id', this._currentFilters.id);
        }
        if (this._currentFilters.title !== undefined) {
          this.setUrlParameter('title', this._currentFilters.title);
        }
        if (this._currentFilters.content !== undefined) {
          this.setUrlParameter('content', this._currentFilters.content);
        }
        if (this._currentFilters.complexity !== undefined) {
          this.setUrlParameter('complexity', this._currentFilters.complexity);
        }
        if (this._currentFilters.machineType !== undefined) {
          this.setUrlParameter('machineType', this._currentFilters.machineType);
        }
        if (this._currentFilters.tag !== undefined) {
          this.setUrlParameter('tag', this._currentFilters.tag);
        }
        if (this._currentFilters.active !== undefined) {
          this.setUrlParameter('active', this._currentFilters.active.toString());
        }
        if (this._currentFilters.isFavorite !== undefined) {
          this.setUrlParameter('isFavorite', this._currentFilters.isFavorite.toString());
        }
        if (this._currentFilters.previewId !== undefined) {
          this.setUrlParameter('previewId', this._currentFilters.previewId);
        }
        const orderBy = this._currentFilters.orderBy || 'title';

        this.setUrlParameter('orderBy', orderBy);

        if (this._currentFilters.direction !== undefined) {
          this.setUrlParameter('direction', this._currentFilters.direction);
        } else {
          if (orderBy === 'title') {
            this.setUrlParameter('direction', 'ASC');
          } else {
            this.setUrlParameter('direction', 'DESC');
          }
        }
        return this.paginate(Learning, pagination.page, pagination.perPage);
      }),
      shareReplay(1)
    );
  }

  refresh(page: number, perPage: number = PER_PAGE) {
    this._currentPaginator = { page, perPage };
    this._refresh$.next(this._currentPaginator);
    return this.allLearning$.pipe(take(1));
  }

  refreshFilters() {
    this._currentFilters = {};
    this.refresh(1, PER_PAGE);
  }

  getList(
    params: {
      page?: number;
      size?: number;
      orderBy?: string;
      direction?: 'ASC' | 'DESC';
      [key: string]: any;
    } = {}
  ): Observable<Paginate<Learning>> {
    Array.from(this.getUrlParameters().keys()).forEach((key) => this.removeUrlParameter(key));

    const currentLanguage = this.languageService.getCurrent().toUpperCase();
    this.setUrlParameter('language', currentLanguage);

    if (params.page) this.setUrlParameter('page', params.page.toString());
    if (params.size) this.setUrlParameter('size', params.size.toString());
    if (params.orderBy) this.setUrlParameter('orderBy', params.orderBy);
    if (params.direction) this.setUrlParameter('direction', params.direction);

    Object.keys(params).forEach((key) => {
      if (
        !['page', 'size', 'orderBy', 'direction'].includes(key) &&
        params[key] !== undefined &&
        params[key] !== null
      ) {
        this.setUrlParameter(key, params[key]);
      }
    });
    return this.paginate(Learning, params.page || 1, params.size || PER_PAGE);
  }
}
